"use client";

import type { LucideIcon } from "lucide-react";
import { RefreshCw, ShieldAlert, TriangleAlert, WifiOff } from "lucide-react";
import { useEffect } from "react";
import { ApiError, NetworkError } from "@/shared/api-client";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { cn } from "@/shared/lib/cn";
import { captureError } from "@/shared/observability/capture";

interface Presentation {
  icon: LucideIcon;
  title: string;
  description: string;
  showRetry: boolean;
}

/** Map any thrown value to a friendly, typed presentation — never a raw stack. */
function present(error: unknown): Presentation {
  if (error instanceof NetworkError) {
    return {
      icon: WifiOff,
      title: "Can't reach the server",
      description: "Check your connection. We'll retry when you're back online.",
      showRetry: true,
    };
  }
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return {
        icon: ShieldAlert,
        title: "You don't have access",
        description: "This resource belongs to another organization or requires a higher role.",
        showRetry: false,
      };
    }
    if (error.status === 404) {
      return {
        icon: TriangleAlert,
        title: "Not found",
        description: "This resource doesn't exist or was removed.",
        showRetry: false,
      };
    }
    return {
      icon: TriangleAlert,
      title: "Something went wrong",
      description: error.message || "The request failed. Try again.",
      showRetry: error.status >= 500,
    };
  }
  return {
    icon: TriangleAlert,
    title: "Something went wrong",
    description: "An unexpected error occurred. Try again.",
    showRetry: true,
  };
}

export function ErrorFallback({
  error,
  reset,
  className,
}: {
  error: unknown;
  reset?: () => void;
  className?: string;
}) {
  const { icon: Icon, title, description, showRetry } = present(error);
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  // A React-boundary catch that window.onerror never sees. Log it with the
  // boundary marker + Next's error digest so an in-render crash (e.g. a
  // hooks #310) is captured with full context, not just the fallback UI.
  useEffect(() => {
    captureError(error, {
      source: "error_boundary",
      digest: (error as { digest?: string })?.digest,
    });
  }, [error]);

  return (
    <Card
      role="alert"
      className={cn("items-center justify-center gap-3 p-8 text-center", className)}
    >
      <span className="flex size-12 items-center justify-center rounded-xl bg-primary/8 text-primary">
        <Icon className="size-5" />
      </span>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
        {requestId ? (
          <p className="font-mono text-xs text-muted-foreground">ref: {requestId}</p>
        ) : null}
      </div>
      {showRetry && reset ? (
        <Button variant="outline" size="sm" onClick={reset}>
          <RefreshCw className="size-3.5" /> Try again
        </Button>
      ) : null}
    </Card>
  );
}
